// Tipos que reflejan el contrato del backend (backend/app/schemas.py).
// Manten estos tipos alineados si el backend cambia.

import type {
  BrandColors,
  BrandFonts,
} from "../../../flyer-renderer/src/flyer/types";

export interface Usuario {
  id: string;
  email: string;
  created_at: string;
}

export interface RespuestaAuth {
  token: string;
  user: Usuario;
}

// draft | generando | listo | fallido | cuota_agotada | budget_exceeded
// (backend/app/models.py + generation_worker.py: FalBudgetExceededError en la
// generacion primaria marca el PROYECTO en "budget_exceeded", M6).
export type EstadoProyecto =
  | "draft"
  | "generando"
  | "listo"
  | "fallido"
  | "cuota_agotada"
  | "budget_exceeded";

// Aspect fijo del flyer (pipeline/flyer_schema.py: FLYER_DIMENSIONS). No es
// un catalogo consultable por API -- es el enum fijo del esquema del flyer
// (M1), a diferencia de model/effort que SI vienen de GET /api/v1/models.
export const FORMATOS_FLYER = ["1:1", "4:5", "9:16", "16:9"] as const;
export type FormatoFlyer = (typeof FORMATOS_FLYER)[number];

export interface ErrorProyecto {
  message?: string;
  details?: unknown;
  [clave: string]: unknown;
}

export interface ProgresoGeneracion {
  stage: string;
  pct: number;
  mensaje?: string | null;
}

// Cadena literal e inmutable dada por el usuario (precio, telefono, fecha,
// termino legal): espejo de pipeline.flyer_agentic.TextoLiteral. El director
// la recibe como entrada FIJA y pipeline/text_gate.py verifica que sobreviva
// caracter por caracter en el flyer final (M6).
export interface TextoLiteral {
  valor: string;
  etiqueta?: string | null;
}

export interface Proyecto {
  id: string;
  brief: string;
  format: string;
  model: string | null;
  effort: string | null;
  budget_max_usd: string | number;
  texto_usuario: TextoLiteral[] | null;
  flyer_document: Record<string, unknown> | null;
  png_url: string | null;
  estado: EstadoProyecto;
  error: ErrorProyecto | null;
  progress: ProgresoGeneracion | null;
  // Presupuesto DERIVADO (M6, backend/app/budget.py): SIEMPRE
  // SUM(usage_records.cost_usd) del proyecto, nunca una columna aparte.
  gastado_usd: string | number;
  remanente_usd: string | number;
  created_at: string;
  updated_at: string;
}

export interface SolicitudCrearProyecto {
  brief: string;
  format: string;
  model?: string | null;
  effort?: string | null;
  budget_max_usd?: number | null;
  texto_usuario?: TextoLiteral[] | null;
}

// PATCH /projects/{id}: parcial, solo mientras el proyecto no esta generando
// (backend/app/schemas.py::UpdateProjectRequest). Se requiere al menos uno.
export interface SolicitudActualizarProyecto {
  brief?: string;
  format?: string;
  model?: string;
  effort?: string;
  budget_max_usd?: number;
  texto_usuario?: TextoLiteral[];
}

// queued | running | succeeded | failed | cancelled | quota_exceeded |
// budget_exceeded (M6, ver pipeline/falai.py::FalBudgetExceededError).
export type EstadoJob =
  | "queued"
  | "running"
  | "succeeded"
  | "failed"
  | "cancelled"
  | "quota_exceeded"
  | "budget_exceeded";

export interface Job {
  id: string;
  project_id: string;
  kind: string;
  status: EstadoJob;
  attempt: number;
  output: Record<string, unknown> | null;
  error: Record<string, unknown> | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
}

export interface EstadoGeneracion {
  estado: EstadoProyecto;
  job: Job | null;
  progress: ProgresoGeneracion | null;
}

// GET /api/v1/models -- fuente unica de aliases de modelo/effort. Nunca
// hardcodear esta lista en el frontend (restriccion vinculante del proyecto).
export interface CatalogoModelos {
  models: string[];
  efforts: string[];
}

// Envelope de error uniforme del backend.
export interface EnvelopeError {
  error: {
    code: string;
    message: string;
    details?: Record<string, unknown> | null;
  };
}

// ---------- Edicion del flyer + revisiones (M4) ----------
// Espejo de backend/app/schemas.py (FlyerPatchResponse/RevisionSummary/
// RevertFlyerResponse/RenderFlyerResponse). El `flyer_document` en si mismo
// se tipa fuerte con el zod de flyer-renderer/src/flyer/types.ts (fuente
// unica del documento), no aqui -- estos tipos solo describen el sobre HTTP.

export interface RespuestaEdicionFlyer {
  project: Proyecto;
  revision_created: number | null;
}

export interface RevisionFlyer {
  version: number;
  created_at: string;
}

// ---------- Re-composicion por formato (M6) ----------
// Espejo de backend/app/schemas.py (RecomposeRequest/FlyerOutputOut).
// `flyer_document` no se tipa fuerte aqui por la misma razon que arriba.

export interface SolicitudRecomponer {
  format: string;
}

export interface SalidaFlyer {
  format: string;
  flyer_document: Record<string, unknown>;
  png_url: string | null;
  created_at: string;
  updated_at: string;
}

export interface RespuestaRevertirFlyer {
  reverted_from: number;
  snapshot_version: number | null;
  project: Proyecto;
}

export interface RespuestaRenderFlyer {
  png_key: string;
  png_url: string;
}

// ---------- Assets + kit de marca (M5b) ----------
// Espejo de backend/app/schemas.py (AssetOut/BrandKitOut/UpdateBrandKitRequest)
// y pipeline/asset_vision.py::AssetAnalysis. `BrandColors`/`BrandFonts` se
// reusan de flyer-renderer/src/flyer/types.ts: son la MISMA forma que
// `FlyerDocument.brand.colors/fonts` (una sola fuente de verdad para el color
// de marca, igual que ya hace PaletaColores.tsx).

export type TipoAsset = "logo" | "foto_producto" | "textura_fondo" | "otro";

export interface ZonaLimpiaAsset {
  x: number;
  y: number;
  ancho: number;
  alto: number;
  descripcion: string;
}

// pipeline.asset_vision.AssetAnalysis. Puede faltar por completo en
// `Asset.analysis` (null) si la vision degrado (timeout/cuota/JSON invalido)
// o si el asset nunca paso por clasificacion.
export interface AnalisisAsset {
  tipo: TipoAsset;
  paleta_dominante: string[];
  zonas_limpias: ZonaLimpiaAsset[];
  descripcion: string;
}

export interface Asset {
  id: string;
  kind: string;
  url: string;
  mime: string;
  size_bytes: number | null;
  width: number | null;
  height: number | null;
  analysis: AnalisisAsset | null;
  created_at: string;
}

export type ModoBrandKit = "con_marca" | "estilo_libre";

export interface BrandKit {
  project_id: string;
  logo_asset_id: string | null;
  colors: BrandColors;
  fonts: BrandFonts | null;
  mode: ModoBrandKit;
}

// PATCH /projects/{id}/brand-kit: al menos uno de los tres campos.
export interface ActualizarBrandKitRequest {
  colors?: BrandColors;
  fonts?: BrandFonts;
  mode?: ModoBrandKit;
}
